/**
 * Global per-RPi agent selector (V3.1 key feature — ARCHITECTURE.md §2).
 *
 * Sits next to the MarketSwitcher in the AppShell header. A multi-select
 * of enrolled agents; empty selection = "All bots". When one or more
 * agents are selected:
 *
 *   - `/v2/orders*`, windows and analytics calls get
 *     `agent_id=<id1>,<id2>` appended (the per-Pi view, ORed across the
 *     selected bots). Scoping is by executing bot, not by its current
 *     config, so orders placed under earlier configs remain included;
 *   - `configVersionIds` (each selected agent's active config for the
 *     CURRENT market, resolved from `/api/v1/fleet/agents/:id/assignments`)
 *     is still exposed on the context for display.
 *
 * The wiring is minimally invasive: the resolved filter is pushed into a
 * module-level store inside `api/client.ts` (`setAgentFilter`) which
 * appends the query params to the relevant GET paths, and all queries
 * are invalidated on change so every page refetches with the new scope.
 * The selection is persisted in localStorage.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { setAgentFilter } from "../api/client";
import { fleetApi, type Assignment, type Bot } from "../api/fleet";
import { useMarket } from "./useMarket";

const STORAGE_KEY = "gmvp.v31.selected_agent_ids";
const LEGACY_STORAGE_KEY = "gmvp.v31.selected_agent_id";

interface AgentSelectorValue {
  /** Selected agent ids; empty = "All bots". */
  agentIds: string[];
  /** The selected agent records (empty when "All bots" or loading). */
  selectedAgents: Bot[];
  /** All enrolled (non-disabled) agents. */
  agents: Bot[];
  /** The selected agents' assignments for the current market. */
  assignments: Assignment[];
  /**
   * Deduplicated union of config_version_ids resolved for
   * (selected agents, current market); empty when nothing is selected or
   * no assignments exist.
   */
  configVersionIds: string[];
  /** True when at least one agent is selected (pages show the badge). */
  isFiltering: boolean;
  setAgentIds: (ids: string[]) => void;
}

const Ctx = createContext<AgentSelectorValue | null>(null);

function readPersisted(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === "string");
    }
    // One-time migration from the single-select key.
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify([legacy]));
      return [legacy];
    }
    return [];
  } catch {
    return [];
  }
}

function writePersisted(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    if (ids.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* best-effort */
  }
}

export function AgentSelectorProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const { marketId } = useMarket();
  const [agentIds, setAgentIdsState] = useState<string[]>(readPersisted);

  const agentsQ = useQuery({
    queryKey: ["fleet", "agents"],
    queryFn: () => fleetApi.agents.list(),
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: false,
  });
  const agents = useMemo(
    () => (agentsQ.data ?? []).filter((a) => a.status !== "disabled"),
    [agentsQ.data],
  );

  // Drop persisted agents that disappeared from the fleet.
  useEffect(() => {
    if (!agentIds.length || !agentsQ.data) return;
    const known = new Set(agentsQ.data.map((a) => a.id));
    const pruned = agentIds.filter((id) => known.has(id));
    if (pruned.length !== agentIds.length) {
      setAgentIdsState(pruned);
      writePersisted(pruned);
    }
  }, [agentIds, agentsQ.data]);

  const idsKey = useMemo(() => [...agentIds].sort().join(","), [agentIds]);

  const assignmentsQ = useQuery({
    queryKey: ["fleet", "agents", idsKey, "assignments"],
    queryFn: async () => {
      const perAgent = await Promise.all(
        agentIds.map((id) => fleetApi.fleet.agentAssignments(id)),
      );
      return perAgent.flat();
    },
    enabled: agentIds.length > 0,
    staleTime: 15_000,
    retry: false,
  });

  const assignments = useMemo<Assignment[]>(() => {
    if (!agentIds.length) return [];
    return (assignmentsQ.data ?? []).filter((a) => a.market_id === marketId);
  }, [agentIds, assignmentsQ.data, marketId]);

  const configVersionIds = useMemo<string[]>(() => {
    const ids = assignments
      .map((a) => a.active_config_version_id)
      .filter((c): c is string => Boolean(c));
    return [...new Set(ids)];
  }, [assignments]);

  const cfgKey = configVersionIds.join(",");

  // Push the resolved filter into the data-api client and refetch
  // everything so the trading pages re-scope.
  useEffect(() => {
    setAgentFilter({ agentIds, configVersionIds });
    void qc.invalidateQueries({
      predicate: (q) => !String(q.queryKey[0] ?? "").startsWith("fleet"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, cfgKey, qc]);

  const setAgentIds = useCallback((ids: string[]) => {
    setAgentIdsState(ids);
    writePersisted(ids);
  }, []);

  const selectedAgents = useMemo(
    () => agents.filter((a) => agentIds.includes(a.id)),
    [agents, agentIds],
  );

  const value = useMemo<AgentSelectorValue>(
    () => ({
      agentIds,
      selectedAgents,
      agents,
      assignments,
      configVersionIds,
      isFiltering: agentIds.length > 0,
      setAgentIds,
    }),
    [agentIds, selectedAgents, agents, assignments, configVersionIds, setAgentIds],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAgentSelector(): AgentSelectorValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAgentSelector must be used inside <AgentSelectorProvider>");
  return v;
}

/**
 * Non-throwing variant for components that may render outside the
 * provider (e.g. isolated tests): returns null instead of throwing.
 */
export function useAgentSelectorOptional(): AgentSelectorValue | null {
  return useContext(Ctx);
}
